import { useState, useCallback } from 'react';
import { spinToWinApi } from '../api/spinToWin';
import type { SpinToWinSettings, SpinToWinEntry } from '../api/spinToWin';

export function useSpinToWin() {
  const [settings, setSettings] = useState<SpinToWinSettings | null>(null);
  const [entriesCount, setEntriesCount] = useState(0);
  const [entries, setEntries] = useState<SpinToWinEntry[]>([]);
  const [entriesPage, setEntriesPage] = useState(1);
  const [entriesLastPage, setEntriesLastPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await spinToWinApi.show();
      setSettings(res.data.data.settings);
      setEntriesCount(res.data.data.entries_count);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load spin-to-win settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(async (next: SpinToWinSettings) => {
    setSaving(true);
    setError(null);
    try {
      const res = await spinToWinApi.update(next);
      setSettings(res.data.data);
      return res.data.data;
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save spin-to-win settings.');
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const fetchEntries = useCallback(async (page = 1) => {
    try {
      const res = await spinToWinApi.entries(page);
      setEntries(res.data.data);
      setEntriesLastPage(res.data.meta.last_page);
      setEntriesPage(page);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load spin entries.');
    }
  }, []);

  return {
    settings, entriesCount, entries, entriesPage, entriesLastPage,
    loading, saving, error, fetchSettings, save, fetchEntries, setError,
  };
}
